import { TReverseParam, extraParams, reverseParams } from 'constants/extraParams';
import { bitSumToArray } from 'utils/numbers';

import { FilterType, TFilterMode } from '../types';

type TReverseMode = TFilterMode['type'];
type TReverseModes = Record<TReverseParam, TReverseMode | null>;
type TReverseSource = Pick<
  FilterType,
  'extraParams' | 'excludeTenders' | 'teamDriving' | 'loggingTruck' | 'roundTrip' | 'permanentCargoApplications' | 'adr'
>;

const modeByBit: Array<{ bit: string; param: TReverseParam; mode: TReverseMode }> = [
  { bit: extraParams.withKoniki, param: reverseParams.loggingTruck, mode: 'include' },
  { bit: extraParams.roundTrip, param: reverseParams.roundTrip, mode: 'include' },
  { bit: extraParams.permanent, param: reverseParams.permanentCargoApplications, mode: 'exclude' },
  { bit: extraParams.adr, param: reverseParams.adr, mode: 'include' },
];

const reverseModeBits = modeByBit.map(({ bit }) => bit);

const modeFieldParams: Array<Exclude<TReverseParam, typeof reverseParams.tenders>> = [
  reverseParams.loggingTruck,
  reverseParams.roundTrip,
  reverseParams.permanentCargoApplications,
  reverseParams.teamDriving,
  reverseParams.adr,
];

const getFilterBits = (filter: TReverseSource) =>
  bitSumToArray(filter.extraParams?.toString()).map(bit => bit.toString());

const getEmptyReverseModes = () =>
  Object.fromEntries(Object.values(reverseParams).map(param => [param, null])) as TReverseModes;

const extractReverseModes = (filter: TReverseSource): TReverseModes => {
  const modes = getEmptyReverseModes();
  const bits = getFilterBits(filter);

  modeByBit.forEach(({ bit, param, mode }) => {
    if (bits.includes(bit)) {
      modes[param] = mode;
    }
  });

  if (filter.excludeTenders) {
    modes[reverseParams.tenders] = 'exclude';
  }

  modeFieldParams.forEach(param => {
    const mode = filter[param]?.type;

    if (mode) {
      modes[param] = mode;
    }
  });

  return modes;
};

const reverseModesToFilterFields = (modes: TReverseModes) => ({
  excludeTenders: modes.tenders === 'exclude',
  ...(modes.loggingTruck && { loggingTruck: { type: modes.loggingTruck } }),
  ...(modes.roundTrip && { roundTrip: { type: modes.roundTrip } }),
  ...(modes.permanentCargoApplications && {
    permanentCargoApplications: { type: modes.permanentCargoApplications },
  }),
  ...(modes.teamDriving && { teamDriving: { type: modes.teamDriving } }),
});

const normalizeFilterModes = <T extends TReverseSource>(filter: T): T => {
  const modes = extractReverseModes(filter);
  const cleanedSum = getFilterBits(filter)
    .filter(bit => !reverseModeBits.includes(bit))
    .reduce((sum, bit) => sum + parseInt(bit, 10), 0);

  return {
    ...filter,
    extraParams: cleanedSum,
    loggingTruck: undefined,
    roundTrip: undefined,
    permanentCargoApplications: undefined,
    teamDriving: undefined,
    ...reverseModesToFilterFields(modes),
    adr: modes.adr ? { type: modes.adr, ids: filter.adr?.ids } : undefined,
  };
};

export { extractReverseModes, getEmptyReverseModes, normalizeFilterModes, reverseModesToFilterFields };
export type { TReverseMode, TReverseModes, TReverseSource };
